import { Button, Divider, Grid, Group, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { cloneDeep, flatMapDeep, sum } from 'lodash';

import { deleteIcon } from '@assets';
import { MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { TransitViewGeneralInfoFormValues } from './validateTransitViewGeneralInfoForm';

interface TransitGuaranteesProps {
  form: UseFormReturnType<TransitViewGeneralInfoFormValues>;
  disabled: boolean;
}

export const TransitGuarantees: React.FC<TransitGuaranteesProps> = ({ form, disabled }) => {
  const guarantees = form.values.Guarantee;
  const codelistSystems = useCodeListSystem({ isTransit: true });

  const handleAddGuarantee = () => {
    form.setFieldValue('Guarantee', [
      ...guarantees,
      {
        guaranteeType: '',
        GuaranteeReference: [
          {
            accessCode: '',
            currency: '',
            GRN: '',
            amountToBeCovered: '',
            setFromCustomer: false,
          },
        ],
      },
    ]);
  };

  const handleRemoveGuarantee = (guaranteeIdx: number) => {
    const newGuarantees = guarantees.filter((_, idx) => idx !== guaranteeIdx);
    form.setFieldValue('Guarantee', newGuarantees);
  };

  const handleAddReference = (guaranteeIdx: number) => {
    const guarantee = guarantees[guaranteeIdx];
    const newReference = {
      accessCode: '',
      currency: '',
      GRN: '',
      amountToBeCovered: '',
      setFromCustomer: false,
    };
    guarantee.GuaranteeReference.push(newReference);
    form.setFieldValue('Guarantee', [...guarantees]);
  };

  const handleDeleteReference = (guaranteeIdx: number, referenceIdx: number) => {
    const guarantee = guarantees[guaranteeIdx];
    guarantee.GuaranteeReference = guarantee.GuaranteeReference.filter((_, idx) => idx !== referenceIdx);
    form.setFieldValue('Guarantee', [...guarantees]);
  };

  const calculateAmount = (guaranteeIdx, referenceIdx) => {
    const Consignment = form.getInputProps('Consignment').value;

    const guaranteesCopy = cloneDeep(guarantees);
    guaranteesCopy[guaranteeIdx].GuaranteeReference[referenceIdx].amountToBeCovered = sum(
      flatMapDeep(Consignment.HouseConsignment || [], (houseConsignment) =>
        (houseConsignment.ConsignmentItem || []).map(
          (consignmentItem) =>
            Number(consignmentItem.Commodity.CommodityCode.initialAmountToPayForDuties || 0) +
            Number(consignmentItem.Commodity.CommodityCode.initialAmountToPayForVat || 0),
        ),
      ),
    ).toFixed(1);

    form.setFieldValue('Guarantee', [...guaranteesCopy]);
  };

  return (
    <Stack mb="xl">
      <Title order={5} fw={600}>
        Guarantees
      </Title>
      {guarantees?.map((guarantee, guaranteeIdx) => (
        <Stack key={`${guarantees.length}-${guaranteeIdx}`}>
          <Grid>
            <Grid.Col span={3}>
              <MultiLangCodes
                {...form.getInputProps(`Guarantee.${guaranteeIdx}.guaranteeType`)}
                label="Guarantee Type"
                system={codelistSystems}
                code="CL251"
                disabled={disabled}
              />
            </Grid.Col>
            {!disabled && (
              <Grid.Col span={1}>
                <Button type="button" onClick={() => handleRemoveGuarantee(guaranteeIdx)} size="lg" variant="transparent">
                  <img src={deleteIcon} alt={'delete'} />
                </Button>
              </Grid.Col>
            )}
          </Grid>
          {guarantee.GuaranteeReference.map((reference, referenceIdx) => (
            <Grid key={referenceIdx}>
              {!reference.setFromCustomer && (
                <Grid.Col span={2}>
                  <TextInput
                    {...form.getInputProps(`Guarantee.${guaranteeIdx}.GuaranteeReference.${referenceIdx}.accessCode`)}
                    label={`Access Code ${referenceIdx + 1}`}
                    disabled={disabled}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={1.5}>
                <MultiLangCodes
                  {...form.getInputProps(`Guarantee.${guaranteeIdx}.GuaranteeReference.${referenceIdx}.currency`)}
                  label={`Currency ${referenceIdx + 1}`}
                  system={codelistSystems}
                  code="CL048"
                  disabled={disabled}
                />
              </Grid.Col>
              <Grid.Col span={1.5}>
                <TextInput
                  {...form.getInputProps(`Guarantee.${guaranteeIdx}.GuaranteeReference.${referenceIdx}.GRN`)}
                  label={`GRN ${referenceIdx + 1}`}
                  disabled={disabled}
                />
              </Grid.Col>
              <Grid.Col span={2.5}>
                <TextInput
                  {...form.getInputProps(`Guarantee.${guaranteeIdx}.GuaranteeReference.${referenceIdx}.amountToBeCovered`)}
                  label={`AmountToBeCovered ${referenceIdx + 1}`}
                  disabled={disabled}
                  data-pw={`guarantee-amount-to-be-covered-${referenceIdx}`}
                />
              </Grid.Col>
              {!disabled && (
                <Grid.Col span={{ base: 2.5, md: 2, lg: 1.5 }}>
                  <Button
                    h={50}
                    type="button"
                    onClick={() => calculateAmount(guaranteeIdx, referenceIdx)}
                    variant="filled"
                    size="compact-md"
                    data-pw={`calculate-guarantee-amount-btn-${referenceIdx}`}
                  >
                    <span style={{ textWrap: 'wrap' }}>Calculate guarantee amount</span>
                  </Button>
                </Grid.Col>
              )}
              {!disabled && referenceIdx === guarantee.GuaranteeReference.length - 1 && (
                <Grid.Col span={1}>
                  <Button fz={30} type="button" onClick={() => handleAddReference(guaranteeIdx)} variant="filled" size="lg">
                    +
                  </Button>
                </Grid.Col>
              )}
              {!disabled && referenceIdx <= guarantee.GuaranteeReference.length - 1 && (
                <Grid.Col span={1}>
                  <Button type="button" onClick={() => handleDeleteReference(guaranteeIdx, referenceIdx)} size="lg" variant="transparent">
                    <img src={deleteIcon} alt={'delete'} />
                  </Button>
                </Grid.Col>
              )}
            </Grid>
          ))}
          {guaranteeIdx < guarantees.length - 1 && <Divider my="md" />}
        </Stack>
      ))}
      {!disabled && (
        <Group>
          <Button type="button" onClick={handleAddGuarantee} disabled={disabled} variant="outline" size="lg">
            Add Guarantee
          </Button>
        </Group>
      )}
    </Stack>
  );
};
